/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { tasksAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import PersonInput, { ME } from '../records/PersonInput'
import { REMINDERS, defaultDue, fromInputDateTime, toInputDateTime } from './taskModel'

/**
 * Create or edit a task. A new task can be tied to a record (recordRef: { id, title }); the
 * assignee defaults to the signed-in member.
 */
const TaskDialog = ({ open, onClose, task, recordRef }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const isNew = !task
  const [title, setTitle] = useState(task?.title || '')
  const [notes, setNotes] = useState(task?.notes || '')
  const [due, setDue] = useState(isNew ? defaultDue() : toInputDateTime(task.dueAt))
  const [reminder, setReminder] = useState(isNew ? '' : (task.remindMinutesBefore ?? ''))
  // "$me" (the person picker's "Me") is sent as no assignee: the server assigns the caller
  const [assignee, setAssignee] = useState(task?.assignee?.id || ME)

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        notes: notes.trim() || null,
        dueAt: fromInputDateTime(due),
        remindMinutesBefore: due && reminder !== '' ? Number(reminder) : null,
        assigneeUserId: assignee && assignee !== ME ? assignee : null,
        recordId: isNew ? recordRef?.id : undefined,
      }
      return isNew ? tasksAPI.create(body) : tasksAPI.update(task.id, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'tasks'] })
      notify(isNew ? 'Task added' : 'Task saved')
      onClose()
    },
  })

  const record = recordRef || task?.record
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => {
        // dialogs render in a portal, but React still bubbles submit to an enclosing form (the record page)
        e.preventDefault(); e.stopPropagation(); if (title.trim()) save.mutate()
      } }}>
      <DialogTitle>{isNew ? 'New task' : 'Edit task'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          {record && <Typography variant="body2" color="text.secondary">For: <strong>{record.title}</strong></Typography>}
          <TextField label="Task" required autoFocus value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 200 }}
            placeholder="e.g. Call back about the quote" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Due" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            <TextField select label="Reminder" value={due ? reminder : ''} disabled={!due}
              SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} onChange={(e) => setReminder(e.target.value)} sx={{ flex: 1 }}
              helperText={due ? ' ' : 'Set a due time first'}>
              {REMINDERS.map((r) => <MenuItem key={String(r.value)} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
          </Stack>
          <PersonInput label="Assigned to" value={assignee} onChange={setAssignee} initialRef={task?.assignee} required />
          <TextField label="Notes" multiline minRows={2} maxRows={8} value={notes} onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 5000 }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={!title.trim() || !assignee || save.isPending}>
          {save.isPending ? 'Saving…' : isNew ? 'Add task' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TaskDialog
